export class ApiError extends Error {
  status: number
  retryAfterMs: number | null
  debugInfo: string | null

  constructor(
    status: number,
    opts?: { bodyMessage?: string; retryAfterMs?: number | null }
  ) {
    super(`HTTP ${status}`)
    this.name = "ApiError"
    this.status = status
    this.retryAfterMs = opts?.retryAfterMs ?? null
    this.debugInfo = opts?.bodyMessage ?? null
  }
}

export class AuthError extends ApiError {
  constructor() {
    super(401)
    this.name = "AuthError"
  }
}

export type ErrorContext =
  | "event"
  | "offers"
  | "flights"
  | "addons"
  | "booking"
  | "booking-request"

export const STATUS_MESSAGES: Record<number, string> = {
  400: "Something went wrong with the request.",
  401: "Session expired. Please log in again.",
  403: "You don't have access to this resource.",
  404: "This resource could not be found.",
  409: "Availability has changed. Please refresh and try again.",
  422: "Some details need correction.",
  429: "Too many requests. Please wait a moment and try again.",
  500: "Service temporarily unavailable. Please try again.",
  502: "Service temporarily unavailable. Please try again.",
  503: "Service temporarily unavailable. Please try again.",
}

export const CONTEXT_MESSAGES: Partial<
  Record<ErrorContext, Partial<Record<number, string>>>
> = {
  booking: {
    409: "The selected offer is no longer valid. Please reselect from a fresh offer.",
  },
  flights: {
    409: "The chosen flight is no longer available. Please select a new flight.",
  },
  "booking-request": {
    400: "Could not submit the booking request. Please try again.",
    429: "Too many requests. Please wait a moment and try again.",
    500: "Service temporarily unavailable. Please try again later.",
    502: "Service temporarily unavailable. Please try again later.",
    503: "Service temporarily unavailable. Please try again later.",
  },
}

const DEFAULT_MESSAGE = "Something went wrong. Please try again."

export function getUserMessage(
  error: unknown,
  context?: ErrorContext
): string {
  if (!(error instanceof ApiError)) {
    if (error instanceof Error) return DEFAULT_MESSAGE
    return DEFAULT_MESSAGE
  }

  const status = error.status

  if (context) {
    const contextOverride = CONTEXT_MESSAGES[context]?.[status]
    if (contextOverride) return contextOverride
  }

  return STATUS_MESSAGES[status] ?? DEFAULT_MESSAGE
}

export function isRetryable(status: number): boolean {
  return status === 429 || status >= 500
}

export function isUserError(status: number): boolean {
  return status >= 400 && status < 500 && status !== 429
}

export interface AsyncError {
  status: number
  userMessage: string
  isRetryable: boolean
  retryAfterMs: number | null
  context: ErrorContext
}

export function toAsyncError(
  error: unknown,
  context: ErrorContext
): AsyncError {
  const status = error instanceof ApiError ? error.status : 0
  return {
    status,
    userMessage: getUserMessage(error, context),
    isRetryable: isRetryable(status),
    retryAfterMs: error instanceof ApiError ? error.retryAfterMs : null,
    context,
  }
}

export function parseRetryAfterMs(header: string | null): number | null {
  if (!header) return null
  const seconds = Number(header)
  if (!Number.isNaN(seconds) && seconds > 0) return seconds * 1000
  const date = Date.parse(header)
  if (!Number.isNaN(date)) {
    const diff = date - Date.now()
    return diff > 0 ? diff : null
  }
  return null
}
